import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';

type Step = 'email' | 'otp' | 'password' | 'done';

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill the email from the login form, if the user already typed one. */
  initialEmail?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({ open, onClose, initialEmail = '' }) => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const reset = () => {
    setStep('email');
    setEmail(initialEmail);
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
    setError('');
    setInfo('');
    setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSendOtp = async () => {
    setError('');
    setInfo('');
    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    try {
      await apiService.generateResetPasswordOtp(email);
      setInfo('OTP sent to your email.');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setInfo('');
    if (otp.trim().length !== 4) {
      setError('Please enter the 4-digit OTP.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiService.validateResetOtp(email, otp.trim());
      if (res?.isOtpValidated && res.token) {
        setResetToken(res.token);
        setStep('password');
      } else {
        setError('Invalid OTP. Please check and try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Could not verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError('');
    setInfo('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await apiService.resetStudioPassword(newPassword, resetToken);
      setStep('done');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const titleByStep: Record<Step, string> = {
    email: 'Reset Password',
    otp: 'Enter OTP',
    password: 'Create New Password',
    done: 'Password Reset',
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {titleByStep[step]}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: colors.textSecondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        {info && !error && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {info}
          </Alert>
        )}

        {step === 'email' && (
          <>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Enter your studio account email and we'll send you a one-time code to reset your password.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
            />
          </>
        )}

        {step === 'otp' && (
          <>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Enter the 4-digit code sent to <strong>{email}</strong>.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
            />
            <Button
              size="small"
              onClick={handleSendOtp}
              disabled={isLoading}
              sx={{ mt: 1, textTransform: 'none', color: colors.primary }}
            >
              Resend OTP
            </Button>
          </>
        )}

        {step === 'password' && (
          <>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
              Choose a strong new password (at least 8 characters).
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
            />
          </>
        )}

        {step === 'done' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: colors.success, mb: 1 }} />
            <Typography variant="body1" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
              Your password has been reset successfully.
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
              You can now sign in with your new password.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'email' && (
          <Button fullWidth variant="contained" onClick={handleSendOtp} disabled={isLoading}>
            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Send OTP'}
          </Button>
        )}
        {step === 'otp' && (
          <Button fullWidth variant="contained" onClick={handleVerifyOtp} disabled={isLoading}>
            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Verify'}
          </Button>
        )}
        {step === 'password' && (
          <Button fullWidth variant="contained" onClick={handleResetPassword} disabled={isLoading}>
            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
          </Button>
        )}
        {step === 'done' && (
          <Button fullWidth variant="contained" onClick={handleClose}>
            Back to Sign In
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
